<template>
  <div class="home-container">
    <!-- Приветственный баннер -->
    <div
      class="welcome-block"
      :style="{ background: welcomeGradient }"
    >
      <div class="welcome-text">
        <p class="welcome-label">Добро пожаловать</p>
        <h1 class="welcome-name">{{ fullName }}</h1>
        <p class="welcome-company" v-if="companyName">
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
            />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          {{ companyName }}
        </p>
      </div>
      <div class="welcome-datetime">
        <div class="welcome-time-row">
          <!-- Иконка времени суток -->
          <svg
            class="welcome-daynight-icon"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            v-html="dayNightIcon"
          />
          <span class="welcome-time">{{ currentTime }}</span>
        </div>
        <span class="welcome-date">{{ currentDate }}</span>
      </div>
    </div>

    <div class="home-sections-row">
      <!-- Быстрые действия -->
      <HomeVehicleQuickActions />

      <HomeSection v-if="visibleMainItems.length" title="Навигация">
        <HomeNavigationAction
          v-for="item in visibleMainItems"
          :key="item.key || item.name"
          :item="item"
          @click="openQuickItem(item)"
        />
      </HomeSection>

      <!-- Инструменты -->
      <CalculatorPresetsWidget />
    </div>
  </div>
</template>

<script>
import { getUserData } from "@/utils/userUtils";

import { usePermissionsStore } from "@/stores/permissionsStore";
import CalculatorPresetsWidget from "@/components/Home/CalculatorPresetsWidget.vue";
import HomeNavigationAction from "@/components/Home/NavigationAction.vue";
import HomeSection from "@/components/Home/Section.vue";
import HomeVehicleQuickActions from "@/components/Home/VehicleQuickActions.vue";

const SUN_ICON = `
  <circle cx="12" cy="12" r="5"/>
  <line x1="12" y1="1" x2="12" y2="3"/>
  <line x1="12" y1="21" x2="12" y2="23"/>
  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
  <line x1="1" y1="12" x2="3" y2="12"/>
  <line x1="21" y1="12" x2="23" y2="12"/>
  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
`;

const MOON_ICON = `
  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
`;

export default {
  name: "HomePage",

  components: {
    CalculatorPresetsWidget,
    HomeNavigationAction,
    HomeSection,
    HomeVehicleQuickActions,
  },

  data() {
    return {
      userData: {},
      currentTime: "",
      currentDate: "",
      currentHour: new Date().getHours(),
      clockTimer: null,

      quickItems: [
        {
          name: "UserProfile",
          pageKey: null,
          section: "main",
          label: "Мой профиль",
          color: "violet",
          iconName: "homeProfile",
        },
        {
          name: "Orders",
          pageKey: "orders",
          section: "main",
          label: "Заказы",
          color: "indigo",
          iconName: "homeOrders",
        },
        {
          name: "Telemetry",
          pageKey: "telemetry",
          section: "main",
          label: "Мониторинг",
          color: "cyan",
          iconName: "homeTelemetry",
        },
        {
          name: "Fleet",
          pageKey: "fleet",
          section: "main",
          label: "Автопарк",
          color: "green",
          iconName: "fleet",
          route: {
            name: "Fleet",
            params: { view: "list" },
          },
        },
        {
          name: "FleetCalendar",
          pageKey: "fleet",
          section: "main",
          label: "Календарь автопарка",
          color: "blue",
          iconName: "calendar",
          route: {
            name: "Fleet",
            params: { view: "calendar" },
          },
        },
        {
          name: "Analytics",
          pageKey: "analytics",
          section: "main",
          label: "Статистика",
          color: "teal",
          iconName: "homeAnalytics",
        },
        {
          name: "Users",
          pageKey: "company_users",
          section: "main",
          label: "Пользователи",
          color: "blue",
          iconName: "homeUsers",
        },
        {
          name: "Integrations",
          pageKey: "integrations",
          section: "main",
          label: "Интеграции",
          color: "amber",
          iconName: "homeIntegrations",
        },
        {
          name: "Settings",
          pageKey: "settings",
          section: "main",
          label: "Настройки компании",
          color: "gray",
          iconName: "homeSettings",
        },
      ],
    };
  },

  computed: {
    permissionsStore() {
      return usePermissionsStore();
    },


    isDay() {
      return this.currentHour >= 8 && this.currentHour < 22;
    },

    dayNightIcon() {
      return this.isDay ? SUN_ICON : MOON_ICON;
    },

    fullName() {
      const first = this.userData.first_name || "";
      const middle = this.userData.middle_name || "";
      return (
        [first, middle].filter(Boolean).join(" ") || "Пользователь"
      );
    },

    companyName() {
      return this.userData.current_company?.name || "";
    },

    welcomeGradient() {
      if (this.isDay) {
        return "linear-gradient(135deg, #0369a1 0%, #19a8eb 100%)";
      }
      return "linear-gradient(135deg, #2d2d4e 0%, #4a4a7a 100%)";
    },

    visibleQuickItems() {
      if (!this.permissionsStore.isLoaded) return [];
      return this.quickItems.filter((item) => {
        if (item.pageKey === null) return true;
        return this.permissionsStore.canSeePage(item.pageKey);
      });
    },

    visibleMainItems() {
      return this.visibleQuickItems.filter((item) => item.section === "main");
    },

  },

  methods: {
    openQuickItem(item) {
      this.$router.push(item.route || { name: item.name });
    },

    updateClock() {
      const now = new Date();
      this.currentHour = now.getHours();
      this.currentTime = now.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
      });
      this.currentDate = now.toLocaleDateString("ru-RU", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
    },
  },

  created() {
    this.userData = getUserData();

    this.updateClock();
    this.clockTimer = setInterval(this.updateClock, 1000);
  },

  beforeUnmount() {
    clearInterval(this.clockTimer);
  },
};
</script>

<style scoped>
.home-container {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent, 30px);
}

.welcome-block {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--base-in-indent, 20px);
  padding: 28px 32px;
  border-radius: 14px;
  color: #fff;
}

.welcome-label {
  font-size: 14px;
  font-weight: 400;
  opacity: 0.8;
  margin-bottom: 6px;
}

.welcome-name {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 8px;
}

.welcome-company {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  opacity: 0.85;
}

.welcome-datetime {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.welcome-time-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.welcome-daynight-icon {
  opacity: 0.9;
  flex-shrink: 0;
}

.welcome-time {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
}

.welcome-date {
  font-size: 14px;
  opacity: 0.8;
  text-transform: capitalize;
}

.home-sections-row {
  display: flex;
  gap: var(--base-out-indent, 30px);
  align-items: flex-start;
}

@media (max-width: 600px) {
  .home-sections-row {
    flex-direction: column;
    align-items: stretch;
  }
}

@media (max-width: 900px) {
  .welcome-block {
    padding: 22px 24px;
  }

  .welcome-time {
    font-size: 22px;
  }
}

@media (max-width: 600px) {
  .welcome-block {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    gap: 30px;
  }

  .welcome-datetime {
    align-items: flex-start;
  }

  .welcome-name {
    font-size: 22px;
  }

}
</style>
